import React, { useRef } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Loader from "@amzn/meridian/loader";
import { Badge } from "@amzn/meridian";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import checkLargeTokens from "@amzn/meridian-tokens/base/icon/check-large";
import { ToteItem } from "src/types/toteTypes";
import ThemedTag from "src/components/pages/configurationDrivenInspections/inspectionReview/ThemedTag";
import "./toteItems.scss";

export interface ToteItemCardProps {
  item: ToteItem;
  onPress: (item: ToteItem) => void;
  isLoading?: boolean;
  onLongPress?: (item: ToteItem) => void;
}

const LONG_PRESS_DELAY_MS = 500;

const ToteItemCard: React.FC<ToteItemCardProps> = React.memo(
  ({ item, onPress, isLoading, onLongPress }) => {
    const primaryImage = item.images?.[0]?.url;
    const isDonationEligible = item.donationEligibilityInformation?.isEligibleForDonation === true;

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressTriggeredRef = useRef(false);

    const startTimer = () => {
      longPressTriggeredRef.current = false;
      if (!onLongPress) return;
      timerRef.current = setTimeout(() => {
        longPressTriggeredRef.current = true;
        timerRef.current = null;
        onLongPress(item);
      }, LONG_PRESS_DELAY_MS);
    };

    const cancelTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleClick = () => {
      if (longPressTriggeredRef.current) {
        longPressTriggeredRef.current = false;
        return;
      }
      onPress(item);
    };

    return (
      <div
        className={`tote-item-long-press${isLoading ? " item-profile-loading" : ""}`}
        onClick={handleClick}
        onContextMenu={(e) => {
          e.preventDefault();
          if (onLongPress) onLongPress(item);
        }}
        onTouchStart={startTimer}
        onTouchEnd={cancelTimer}
        onTouchMove={cancelTimer}
        onTouchCancel={cancelTimer}
      >
        <Column className="home-card" spacing="none">
          <Row
            alignmentVertical="center"
            alignmentHorizontal="justify"
            spacingInset="300 400"
            spacing="400"
          >
            <div className="tote-item-image-box">
              <img src={primaryImage} alt={item.name ?? "Product"} className="tote-item-image" />
              <div className="tote-item-badge">
                <Badge value={item.quantity} />
              </div>
            </div>

            <Column spacing="100" width="fill">
              {isDonationEligible && (
                <ThemedTag
                  text="Eligible for donation"
                  type="success"
                  iconTokens={checkLargeTokens}
                />
              )}
              <Text type="b300" className="tote-item-name">
                {item.name ?? "Unknown Product"}
              </Text>
              {item.asin && (
                <Text type="b200" color="secondary">
                  ASIN: {item.asin}
                </Text>
              )}
            </Column>

            {isLoading ? (
              <Loader type="circular" size="small" />
            ) : (
              <Icon tokens={chevronRightSmallTokens} />
            )}
          </Row>
        </Column>
      </div>
    );
  },
);

export default ToteItemCard;
